import React, { useMemo, useCallback } from 'react';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { SbTable, SbButton, SbSelect, SbSwitch, SbTooltip, TooltipTriggerStyled } from 'uiCore';
import { ROLE_REPORTING_PAGE_SIZE, ROLE_REPORTING_PAGE_SIZE_OPTIONS } from 'constants/roleReporting.constants';
import {
  RoleReportingSectionStyled,
  RoleReportingTableWrapperStyled,
  StatusBadgeStyled,
  ApexTagStyled,
  ManagerCellText,
  EditCellWrapper,
  EditIconButton,
  ManagerEditWrapper,
  ApexSwitchRow,
} from './RoleReporting.styles';

const TOOLTIP_EDIT_ROLE_REPORTING = 'Assign a reporting manager or mark this role as an Apex Role.';

// What the Reporting To cell reads as, outside edit mode (used for its filter).
const getReportingToLabel = (record) =>
  record?.is_apex_role ? 'Apex Role' : record?.role_reporting_to?.job_title || 'Unassigned';

const RoleReportingTable = ({
  dataSource = [],
  loading = false,
  tableFilters = {},
  onTableChange,
  filterOptions = {},
  managerOptionsFor,
  editingRowId,
  editValues,
  onEditStart,
  onEditCancel,
  onManagerChange,
  onApexChange,
  onEditSave,
  savingRowId,
}) => {
  const safeData = useMemo(
    () => (dataSource || []).filter((row) => row && (row.sb_unique_id || row.job_id)),
    [dataSource],
  );

  const isEditing = useCallback((record) => record?.sb_unique_id === editingRowId, [editingRowId]);

  const columns = useMemo(
    () => [
      {
        title: 'Job Title',
        dataIndex: 'job_title',
        key: 'job_title',
        filteredValue: tableFilters.job_title || null,
        render: (text) => text || '-',
      },
      {
        title: 'Job Code',
        dataIndex: 'job_code',
        key: 'job_code',
        filteredValue: tableFilters.job_code || null,
        render: (text) => text || '-',
      },
      {
        title: 'Requisition #',
        dataIndex: 'sb_requisition_number',
        key: 'sb_requisition_number',
        filteredValue: tableFilters.sb_requisition_number || null,
        render: (text) => text || '-',
      },
      {
        title: 'Status',
        dataIndex: 'jd_status',
        key: 'jd_status',
        filters: filterOptions.jd_status,
        filteredValue: tableFilters.jd_status || null,
        onFilter: (value, record) => record?.jd_status === value,
        render: (text) => (text ? <StatusBadgeStyled $status={text}>{text}</StatusBadgeStyled> : '-'),
      },
      {
        title: 'Business Unit',
        dataIndex: 'business_unit',
        key: 'business_unit',
        filters: filterOptions.business_unit,
        filteredValue: tableFilters.business_unit || null,
        onFilter: (value, record) => record?.business_unit === value,
        render: (text) => text || '-',
      },
      {
        title: 'Function',
        dataIndex: 'function',
        key: 'function',
        filters: filterOptions.function,
        filteredValue: tableFilters.function || null,
        onFilter: (value, record) => record?.function === value,
        render: (text) => text || '-',
      },
      {
        title: 'Level',
        dataIndex: 'job_level',
        key: 'job_level',
        filters: filterOptions.job_level,
        filteredValue: tableFilters.job_level || null,
        onFilter: (value, record) => record?.job_level === value,
        render: (text) => text || '-',
      },
      {
        title: 'Location',
        dataIndex: 'location',
        key: 'location',
        filteredValue: tableFilters.location || null,
        render: (text) => text || '-',
      },
      {
        title: 'Reporting To',
        key: 'role_reporting_to',
        width: 260,
        filters: [...new Set(safeData.map(getReportingToLabel))]
          .sort((a, b) => a.localeCompare(b))
          .map((label) => ({ text: label, value: label })),
        filteredValue: tableFilters.role_reporting_to || null,
        onFilter: (value, record) => getReportingToLabel(record) === value,
        render: (_, record) => {
          if (isEditing(record)) {
            const options = managerOptionsFor(record.sb_unique_id);
            return (
              <ManagerEditWrapper>
                <SbSelect
                  showSearch
                  allowClear
                  placeholder="Select reporting manager"
                  options={options}
                  value={editValues?.managerId || undefined}
                  disabled={editValues?.isApexRole}
                  onChange={onManagerChange}
                  optionFilterProp="label"
                  size="small"
                />
                <ApexSwitchRow>
                  <SbSwitch
                    size="small"
                    checked={!!editValues?.isApexRole}
                    onChange={onApexChange}
                  />
                  Apex Role (no manager)
                </ApexSwitchRow>
              </ManagerEditWrapper>
            );
          }
          if (record.is_apex_role) {
            return <ApexTagStyled>Apex Role</ApexTagStyled>;
          }
          if (record.role_reporting_to?.job_title) {
            return (
              <ManagerCellText>
                {record.role_reporting_to.job_title}
                {record.role_reporting_to.job_code && (
                  <span className="manager-code">({record.role_reporting_to.job_code})</span>
                )}
              </ManagerCellText>
            );
          }
          return <ManagerCellText>Unassigned</ManagerCellText>;
        },
      },
      {
        title: 'Action',
        key: 'action',
        width: 90,
        render: (_, record) => {
          if (isEditing(record)) {
            const isSaving = savingRowId === record.sb_unique_id;
            return (
              <EditCellWrapper>
                <SbButton
                  buttonType="primary"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => onEditSave(record)}
                  loading={isSaving}
                  disabled={isSaving}
                />
                <SbButton
                  buttonType="secondary"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={onEditCancel}
                  disabled={isSaving}
                />
              </EditCellWrapper>
            );
          }
          return (
            <SbTooltip content={TOOLTIP_EDIT_ROLE_REPORTING}>
              <TooltipTriggerStyled>
                <EditIconButton
                  type="button"
                  onClick={() => onEditStart(record)}
                  aria-label="Edit role reporting"
                >
                  <EditOutlined />
                </EditIconButton>
              </TooltipTriggerStyled>
            </SbTooltip>
          );
        },
      },
    ],
    [safeData, tableFilters, filterOptions, editValues, isEditing, managerOptionsFor, onManagerChange, onApexChange, onEditStart, onEditCancel, onEditSave, savingRowId],
  );

  return (
    <RoleReportingSectionStyled>
      <RoleReportingTableWrapperStyled>
        <SbTable
          columns={columns}
          dataSource={safeData}
          loading={loading}
          rowKey={(record, index) => record?.sb_unique_id ?? index}
          sticky
          rowClassName={(record) => (isEditing(record) ? 'rr-editing-row' : '')}
          onChange={onTableChange}
          pagination={{
            defaultPageSize: ROLE_REPORTING_PAGE_SIZE,
            pageSizeOptions: ROLE_REPORTING_PAGE_SIZE_OPTIONS,
            showSizeChanger: true,
            size: 'small',
          }}
        />
      </RoleReportingTableWrapperStyled>
    </RoleReportingSectionStyled>
  );
};

export default RoleReportingTable;
